import React, { useState } from 'react';
import { Form, Input, Button, Card, message, Typography, Divider } from 'antd';
import { UserOutlined, LockOutlined, EyeInvisibleOutlined, EyeTwoTone } from '@ant-design/icons';
import Api from '../Network';
import { LoginProps } from '../types/types';
import { images } from '../assets';
import Logo from '../Component/Logo';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../Component/LanguageSwitcher';
import ForgotPassword from './ForgotPassword';

const { Title, Text } = Typography;

const Login: React.FC<LoginProps> = ({ setUser }) => {
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const { t } = useTranslation();

  // Generate Firebase FCM token using Firebase messaging
  const generateFirebaseToken = async (): Promise<string | null> => {
    try {
      // Check if browser supports notifications
      if (!('Notification' in window)) {
        return null;
      }

      // Check if we're in a secure context (HTTPS or localhost)
      if (!window.isSecureContext) {
        return null;
      }

      // Request permission if not granted
      if (Notification.permission === 'default') {
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') {
          return null;
        }
      }

      // Import Firebase modules dynamically
      try {
        const { initializeApp } = await import('firebase/app');
        const { getMessaging, getToken } = await import('firebase/messaging');

        // Import centralized Firebase configuration
        const { firebaseConfig, vapidKey } = await import('../config/firebase');

        // Check if Firebase config is valid
        if (!firebaseConfig || !firebaseConfig.apiKey) {
          return null;
        }

        // Check if VAPID key is configured (should be a real VAPID key, not placeholder)
        if (!vapidKey || vapidKey.includes('YOUR_VAPID_KEY')) {
          return null;
        }

        // Initialize Firebase app
        const app = initializeApp(firebaseConfig);
        const messaging = getMessaging(app);

        // Get FCM token using Firebase messaging with error handling
        try {
          const token = await getToken(messaging, {
            vapidKey: vapidKey,
          });

          if (token) {
            return token;
          } else {
            return null;
          }
        } catch (tokenError: any) {
          return null;
        }
      } catch (firebaseError) {
        return null;
      }
    } catch (error) {
      return null;
    }
  };

  // Get FCM token for push notifications
  const getFCMToken = async (): Promise<string | null> => {
    try {
      // Use the Firebase token generation
      const token = await generateFirebaseToken();
      return token;
    } catch (error) {
      return null;
    }
  };

  const onFinish = async (values: { phone_number: string; password: string }) => {
    setLoading(true);
    try {
      // Clear all cache and previous user data before login
      localStorage.removeItem('token');
      localStorage.removeItem('userInfo');
      localStorage.removeItem('permissions');
      localStorage.removeItem('roles');
      sessionStorage.clear();

      // Get device token for push notifications
      const deviceToken = await getFCMToken();

      // Prepare login data with device token (only if token is available)
      const loginData = {
        ...values,
        ...(deviceToken && { device_token: deviceToken }),
      };

      const response: any = await Api.post('/api/admin/login', loginData);
      if (response && response.data.token) {
        // Store new token
        localStorage.setItem('token', response.data.token);

        // Store user data with roles and permissions
        const userData = response.data.user;
        localStorage.setItem('userInfo', JSON.stringify(userData));

        // Update Redux state
        setUser(userData);

        message.success(t('auth.loginSuccess'));

        // Force reload to ensure all components get fresh data
        window.location.href = '/';
      } else {
        message.error(t('auth.loginFailed'));
      }
    } catch (error) {
      console.error('Login error:', error);
      message.error(t('auth.loginFailed'));
    } finally {
      setLoading(false);
    }
  };

  // Show forgot password form if requested
  if (showForgotPassword) {
    return <ForgotPassword onBack={() => setShowForgotPassword(false)} />;
  }

  return (
    <div className="responsive-login">
      <div
        className="login-bg-pattern"
        style={{ backgroundImage: `url(${images.bg})` }}
        aria-hidden
      />

      <div className="login-branding">
        <div className="login-branding-inner">
          <Logo size="medium" invert={true} style={{ marginBottom: '30px' }} />
          <Title level={3} className="brand-subtitle">
            {t('branding.retailManagement')}
          </Title>
        </div>
      </div>

      <div className="login-form">
        <Card>
          <div className="login-form-header">
            <Title level={2}>{t('auth.welcomeBack')}</Title>
            <Text type="secondary">{t('auth.signInToContinue')}</Text>
          </div>

          <Form name="login" onFinish={onFinish} autoComplete="off" layout="vertical" size="large">
            <Form.Item
              name="phone_number"
              rules={[
                { required: true, message: t('validation.required') },
                { pattern: /^[0-9+\-\s()]+$/, message: t('validation.invalidPhone') },
              ]}
            >
              <Input prefix={<UserOutlined />} placeholder={t('auth.phoneNumber')} />
            </Form.Item>

            <Form.Item
              name="password"
              rules={[{ required: true, message: t('validation.required') }]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder={t('auth.password')}
                iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
              />
            </Form.Item>

            <div className="login-forgot-row">
              <Button type="link" onClick={() => setShowForgotPassword(true)}>
                {t('auth.forgotPassword')}
              </Button>
            </div>

            <Form.Item style={{ marginBottom: '24px' }}>
              <Button type="primary" htmlType="submit" loading={loading} block>
                {loading ? t('auth.signingIn') : t('auth.login')}
              </Button>
            </Form.Item>

            <Divider style={{ margin: '24px 0' }}>
              <Text type="secondary">{t('auth.secureLogin')}</Text>
            </Divider>

            <div className="login-secure-note">
              <Text type="secondary">🔒 {t('auth.dataProtected')}</Text>
            </div>
          </Form>
        </Card>
      </div>

      <div className="login-language-switcher">
        <LanguageSwitcher type="button" />
      </div>
    </div>
  );
};

export default Login;
